import type { Amenity } from "@/types/amenity";

/** Places in the amenities row of the guest home screen. */
export const HOME_ROW_SIZE = 4;

export type AmenityHomeState =
  | { kind: "shown"; position: number }
  | { kind: "inactive" }
  | { kind: "noPhoto" }
  | { kind: "overflow" };

type HomeRowAmenity = Pick<Amenity, "isActive" | "photoId">;
type HomeStarAmenity = Pick<Amenity, "photoId" | "showOnHome">;

export const AMENITY_HOME_STATE_KEYS: Record<AmenityHomeState["kind"], string> =
  {
    shown: "amenities.home.state.shown",
    inactive: "amenities.home.state.inactive",
    noPhoto: "amenities.home.state.noPhoto",
    overflow: "amenities.home.state.overflow",
  };

/**
 * Starred amenities in home order: the guest sees the first four active ones with a photo,
 * the others stay hidden with the reason.
 */
export const getAmenityHomeStates = (
  pinned: HomeRowAmenity[],
): AmenityHomeState[] => {
  let position = 0;
  return pinned.map((amenity): AmenityHomeState => {
    if (!amenity.isActive) return { kind: "inactive" };
    if (!amenity.photoId) return { kind: "noPhoto" };
    if (position >= HOME_ROW_SIZE) return { kind: "overflow" };
    position += 1;
    return { kind: "shown", position };
  });
};

/** A star goes on only with a photo; a set star can always be removed. */
export const canToggleHomeStar = (
  amenity: HomeStarAmenity,
  canEdit: boolean,
): boolean => canEdit && (!!amenity.showOnHome || !!amenity.photoId);

/** i18n key of the star button: remove, add, or why it cannot be added. */
export const getHomeStarLabelKey = (amenity: HomeStarAmenity): string => {
  if (amenity.showOnHome) return "amenities.table.starOff";
  return amenity.photoId
    ? "amenities.table.starOn"
    : "amenities.table.starNeedsPhoto";
};

/** Items in the order of `ids`; items missing from `ids` keep their order after them. */
export const orderAmenitiesByIds = <T extends { id: string }>(
  items: T[],
  ids: string[],
): T[] => {
  const position = new Map(ids.map((id, index) => [id, index]));
  const listed = items
    .filter((item) => position.has(item.id))
    .sort((a, b) => (position.get(a.id) ?? 0) - (position.get(b.id) ?? 0));
  const rest = items.filter((item) => !position.has(item.id));
  return [...listed, ...rest];
};
